/**
 * Admin "Login as" / "View as" – test features as another user.
 * Shows read-only profile, stats, registrations. All actions logged to audit.
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useRole } from '../contexts/RoleContext';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { getPlayerProfileData } from '../api/readApi';
import './AdminViewAsUser.css';

/**
 * Admin "View as" / "Login as" – visits this route start app-wide impersonation and redirect to home.
 * Shows loading/details briefly, then starts impersonation and navigates.
 */
export default function AdminViewAsUser() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { isAdmin, isSuperAdmin } = useRole();
  const { startImpersonating } = useImpersonation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    if (!userId || !currentUser?.uid) return;
    setLoading(true);
    getPlayerProfileData(userId)
      .then((res) => setData(res))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [userId, currentUser?.uid]);

  useEffect(() => {
    if (!userId || !data?.profile || started) return;
    setStarted(true);
    const profile = {
      displayName: data.profile.displayName || data.profile.email || 'Unknown',
      email: data.profile.email || '',
      photoURL: data.profile.photoURL || data.profile.avatarURL || null,
    };
    startImpersonating(userId, profile);
    navigate('/', { replace: true });
  }, [userId, data?.profile, started, startImpersonating, navigate]);

  if (!isAdmin && !isSuperAdmin) {
    navigate('/dashboard');
    return null;
  }

  if (loading) return <div className="admin-view-as-loading">Loading user…</div>;
  if (!data?.profile) return <div className="admin-view-as-error">User not found</div>;

  return <div className="admin-view-as-loading">Starting Login as…</div>;
}
